/* A panel with a heading you can recognise and an explanation you can ask for.
 *
 * Nearly every panel in the app opened with a paragraph: why sleep is a wall,
 * why hobbies are claimed by sessions, why sharing is per category. Every one
 * of those paragraphs is true and worth having — once. On the fortieth visit
 * it is a block of grey text between somebody and the control they came for.
 *
 * So the heading carries an icon and a one-line summary, and the paragraph
 * moves behind an ⓘ that says what it is ("About sleep"). The first-time reader
 * is one tap from the reasoning; everyone else gets the screen.
 *
 * The heading keeps its id and its words exactly — the tour points at ids, and
 * a screen reader announces the words — the icon is decoration beside them.
 */

import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import Icon from './Icon';
import type { IconName } from './Icon';

export default function Section({ id, icon, title, meta, info, action, className = '', children }: {
  id?: string;
  icon: IconName;
  title: ReactNode;
  /** One short line beside the heading — what this panel is, at a glance. */
  meta?: ReactNode;
  /** The explanation, hidden until asked for. */
  info?: ReactNode;
  action?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  const auto = useId();
  const headingId = id ?? `${auto}-title`;

  return (
    <section className={`panel section ${className}`.trim()} aria-labelledby={headingId}>
      <div className="section-head">
        <span className="section-icon" aria-hidden="true"><Icon name={icon} size={18} /></span>
        <div className="section-titles">
          <h2 id={headingId}>{title}</h2>
          {meta && <span className="muted small section-meta">{meta}</span>}
        </div>
        <div className="section-tools">
          {info && <InfoToggle about={typeof title === 'string' ? title : 'this'} text={info} />}
          {action}
        </div>
      </div>
      {children}
    </section>
  );
}

/* The ⓘ and what it opens. Rendered into the heading row as a button, with the
   text placed after the row so it reads in order: heading, explanation, body. */
export function InfoToggle({ about, text }: { about: string; text: ReactNode }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <>
      <button type="button" className={`btn btn-ghost btn-icon info-toggle${open ? ' is-on' : ''}`}
              aria-expanded={open} aria-controls={id}
              aria-label={`About ${about.toLowerCase()}`} title={`About ${about.toLowerCase()}`}
              onClick={() => setOpen(o => !o)}>
        <Icon name="info" size={17} />
      </button>
      {open && <p id={id} className="section-info muted small">{text}</p>}
    </>
  );
}
